import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash, PencilSimple, Plus, Eye, ArrowLeft, Lock } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_KEY = process.env.REACT_APP_ADMIN_SECRET || "madlydigital-admin-2025";

const authHeaders = { "x-admin-key": ADMIN_KEY };

const EMPTY_FORM = {
  title: "",
  slug: "",
  excerpt: "",
  content: "",
  author: "Madly Digital",
  category: "",
  cover_image: "",
  read_minutes: 5,
};

function formatDate(d) {
  try {
    return new Date(d).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

/** Simple JS-level passcode gate — keeps casual visitors out */
function PasscodeGate({ onUnlock }) {
  const [input, setInput] = useState("");
  const [shake, setShake] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (input.trim() === ADMIN_KEY) {
      onUnlock();
    } else {
      setShake(true);
      setTimeout(() => setShake(false), 600);
      toast.error("Wrong admin key.");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="glass rounded-3xl p-10 md:p-14 w-full max-w-sm text-center">
        <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-[#e11d48]/10 border border-[#e11d48]/30 mb-6 mx-auto">
          <Lock size={24} className="text-[#e11d48]" />
        </div>
        <h1 className="font-display text-3xl text-[#0b2e5e]">Admin Access</h1>
        <p className="text-[#0b2e5e]/55 text-sm mt-2 mb-8">
          Enter your admin key to manage blog posts.
        </p>
        <form onSubmit={handleSubmit} className={shake ? "animate-[shake_0.5s]" : ""}>
          <input
            type="password"
            className="ae-input mb-4"
            placeholder="Admin key"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            autoFocus
          />
          <button type="submit" className="btn-primary w-full justify-center">
            Unlock
          </button>
        </form>
        <Link to="/" className="inline-flex items-center gap-1 text-[#0b2e5e]/45 text-sm mt-6 hover:text-[#0b2e5e] transition-colors">
          <ArrowLeft size={14} /> Back to site
        </Link>
      </div>
    </div>
  );
}

export default function AdminBlog() {
  const [unlocked, setUnlocked] = useState(false);
  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [view, setView] = useState("list"); // "list" | "new" | "edit"
  const [editingPost, setEditingPost] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const fetchPosts = useCallback(async () => {
    setLoadingPosts(true);
    try {
      const { data } = await axios.get(`${API}/blog`);
      setPosts(data);
    } catch {
      toast.error("Couldn't load posts.");
    } finally {
      setLoadingPosts(false);
    }
  }, []);

  useEffect(() => {
    if (unlocked) fetchPosts();
  }, [unlocked, fetchPosts]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: name === "read_minutes" ? Number(value) : value }));
  };

  const openNew = () => {
    setForm(EMPTY_FORM);
    setEditingPost(null);
    setView("new");
  };

  const openEdit = (post) => {
    setEditingPost(post);
    setForm({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt,
      content: post.content,
      author: post.author,
      category: post.category,
      cover_image: post.cover_image || "",
      read_minutes: post.read_minutes,
    });
    setView("edit");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.excerpt || !form.content || !form.author || !form.category) {
      toast.error("Please fill all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      if (view === "new") {
        await axios.post(`${API}/blog`, form, { headers: authHeaders });
        toast.success("Blog post created!");
      } else {
        await axios.put(`${API}/blog/${editingPost.slug}`, form, { headers: authHeaders });
        toast.success("Blog post updated!");
      }
      await fetchPosts();
      setView("list");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (slug, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/blog/${slug}`, { headers: authHeaders });
      toast.success("Post deleted.");
      setPosts((p) => p.filter((post) => post.slug !== slug));
    } catch {
      toast.error("Couldn't delete the post.");
    }
  };

  if (!unlocked) return <PasscodeGate onUnlock={() => setUnlocked(true)} />;

  return (
    <div className="min-h-screen pt-28 pb-20 px-6 md:px-12 max-w-6xl mx-auto">
      {/* ── Header ── */}
      <div className="flex items-center justify-between flex-wrap gap-4 mb-10">
        <div>
          <span className="issue-stripe">§ Admin</span>
          <h1 className="font-display text-4xl md:text-5xl text-[#0b2e5e] mt-2">
            Blog Manager
          </h1>
        </div>
        <div className="flex items-center gap-3">
          {view !== "list" && (
            <button onClick={() => setView("list")} className="btn-ghost">
              <ArrowLeft size={16} /> Back to list
            </button>
          )}
          {view === "list" && (
            <button onClick={openNew} className="btn-primary" data-testid="admin-new-post">
              <Plus weight="bold" size={16} /> New post
            </button>
          )}
          <Link to="/blog" target="_blank" className="btn-ghost">
            <Eye size={16} /> View blog
          </Link>
        </div>
      </div>

      {/* ── Post List ── */}
      {view === "list" && (
        <div>
          {loadingPosts && (
            <div className="text-center py-20 text-[#0b2e5e]/50 font-mono-mini">Loading posts…</div>
          )}
          {!loadingPosts && posts.length === 0 && (
            <div className="text-center py-20">
              <p className="font-display text-2xl text-[#0b2e5e]/30">No posts yet.</p>
              <button onClick={openNew} className="btn-primary mt-6">
                <Plus weight="bold" size={16} /> Create your first post
              </button>
            </div>
          )}
          {!loadingPosts && posts.length > 0 && (
            <div className="space-y-4">
              {posts.map((post) => (
                <div
                  key={post.id}
                  className="glass rounded-2xl p-6 flex items-center gap-6 flex-wrap"
                >
                  {/* Cover thumbnail */}
                  {post.cover_image && (
                    <div className="h-16 w-24 rounded-xl overflow-hidden flex-shrink-0">
                      <img
                        src={post.cover_image}
                        alt={post.title}
                        className="h-full w-full object-cover"
                      />
                    </div>
                  )}

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="tag text-xs">{post.category}</span>
                      <span className="text-[#0b2e5e]/40 text-xs font-mono-mini">
                        {formatDate(post.published_at)}
                      </span>
                    </div>
                    <h3 className="font-display text-xl text-[#0b2e5e] leading-tight truncate">
                      {post.title}
                    </h3>
                    <p className="text-[#0b2e5e]/50 text-sm mt-1 line-clamp-1">{post.excerpt}</p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Link
                      to={`/blog/${post.slug}`}
                      target="_blank"
                      className="btn-ghost px-4 py-2 text-sm"
                      title="Preview"
                    >
                      <Eye size={16} />
                    </Link>
                    <button
                      onClick={() => openEdit(post)}
                      className="btn-ghost px-4 py-2 text-sm"
                      title="Edit"
                    >
                      <PencilSimple size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(post.slug, post.title)}
                      className="btn-ghost px-4 py-2 text-sm text-[#e11d48] border-[#e11d48]/30 hover:bg-[#e11d48]/5"
                      title="Delete"
                    >
                      <Trash size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Create / Edit Form ── */}
      {(view === "new" || view === "edit") && (
        <form onSubmit={handleSubmit} className="glass rounded-3xl p-8 md:p-10 space-y-6">
          <h2 className="font-display text-2xl text-[#0b2e5e]">
            {view === "new" ? "New Blog Post" : `Editing: ${editingPost?.title}`}
          </h2>

          {/* Title + Category */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="font-mono-mini text-[#0b2e5e]/55 mb-2 block">
                Title <span className="text-[#e11d48]">*</span>
              </label>
              <input
                name="title"
                value={form.title}
                onChange={onChange}
                className="ae-input"
                placeholder="Post title"
              />
            </div>
            <div>
              <label className="font-mono-mini text-[#0b2e5e]/55 mb-2 block">
                Category <span className="text-[#e11d48]">*</span>
              </label>
              <input
                name="category"
                value={form.category}
                onChange={onChange}
                className="ae-input"
                placeholder="e.g. Digital Marketing, Trends"
              />
            </div>
          </div>

          {/* Slug + Author */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="font-mono-mini text-[#0b2e5e]/55 mb-2 block">
                Slug{" "}
                <span className="text-[#0b2e5e]/35 normal-case font-mono text-[10px]">
                  (auto-generated if empty)
                </span>
              </label>
              <input
                name="slug"
                value={form.slug}
                onChange={onChange}
                className="ae-input"
                placeholder="my-post-slug"
                disabled={view === "edit"}
              />
            </div>
            <div>
              <label className="font-mono-mini text-[#0b2e5e]/55 mb-2 block">
                Author <span className="text-[#e11d48]">*</span>
              </label>
              <input
                name="author"
                value={form.author}
                onChange={onChange}
                className="ae-input"
                placeholder="Author name"
              />
            </div>
          </div>

          {/* Cover Image + Read time */}
          <div className="grid md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="font-mono-mini text-[#0b2e5e]/55 mb-2 block">Cover image URL</label>
              <input
                name="cover_image"
                value={form.cover_image}
                onChange={onChange}
                className="ae-input"
                placeholder="https://..."
              />
              {form.cover_image && (
                <img
                  src={form.cover_image}
                  alt="preview"
                  className="mt-2 h-20 w-full object-cover rounded-xl border border-[#0b2e5e]/10"
                  onError={(e) => (e.target.style.display = "none")}
                />
              )}
            </div>
            <div>
              <label className="font-mono-mini text-[#0b2e5e]/55 mb-2 block">
                Read time (minutes)
              </label>
              <input
                name="read_minutes"
                type="number"
                min={1}
                max={120}
                value={form.read_minutes}
                onChange={onChange}
                className="ae-input"
              />
            </div>
          </div>

          {/* Excerpt */}
          <div>
            <label className="font-mono-mini text-[#0b2e5e]/55 mb-2 block">
              Excerpt <span className="text-[#e11d48]">*</span>
            </label>
            <textarea
              name="excerpt"
              value={form.excerpt}
              onChange={onChange}
              rows={2}
              className="ae-input resize-none"
              placeholder="Short summary shown on the blog listing page…"
            />
          </div>

          {/* Content */}
          <div>
            <label className="font-mono-mini text-[#0b2e5e]/55 mb-2 block">
              Content <span className="text-[#e11d48]">*</span>
              <span className="ml-2 text-[#0b2e5e]/35 normal-case font-mono text-[10px]">
                Supports: ## Heading, - bullet, **bold**, numbered lists (1.)
              </span>
            </label>
            <textarea
              name="content"
              value={form.content}
              onChange={onChange}
              rows={20}
              className="ae-input resize-y font-mono text-sm leading-relaxed"
              placeholder={`## Introduction\n\nYour first paragraph here...\n\n## Section Heading\n\n- Bullet point one\n- Bullet point two\n\n**Bold text** for emphasis.`}
            />
          </div>

          {/* Submit */}
          <div className="flex items-center justify-between flex-wrap gap-4 pt-2">
            <button
              type="button"
              onClick={() => setView("list")}
              className="btn-ghost"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="admin-submit-post"
            >
              {submitting
                ? view === "new"
                  ? "Publishing…"
                  : "Saving…"
                : view === "new"
                ? "Publish post"
                : "Save changes"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
